import React, { useContext, useState } from 'react'
import { Wrapper, ModalBody, FooterActions } from './DropdownStyles'
import LiveModal from 'live/components/ui/Modal'
import SpanInput from 'live/components/time/ui/span-input'
import CustomCalendar from '../calendar/custom-calendar'
import moment from 'moment'
import { DashboardContext } from 'live/context/dashboard'
import i18n from 'live/services/i18n'

import { SpanPickerContext } from '../utils/initialStates'
import { customDatesExpression, validatePipeExpression } from '../OperationSpanPicker/OperationConfigs'
import { dispatchRefreshEvent, updateIdentityLookup } from '../utils/RefreshFunctions'
import { useSetState } from '@mantine/hooks'
import { parseOptions, stringToDateTime } from '../utils/utils'
import styled from 'styled-components'

const TimespanSelect = () => {
    const { actions, ...context } = useContext(DashboardContext)
    const { headerContext, operationsPickerConfigs, changeSpanPickerConfig } = useContext(SpanPickerContext)
    const [sDate, sTime, eDate, eTime] = stringToDateTime(context?.state?.currentSpan ?? operationsPickerConfigs.defaultSpan)
    const [definedSpan, setDefinedSpan] = useState(context?.state?.currentSpan ?? operationsPickerConfigs.defaultSpan)
    const [state, setState] = useSetState({
        startDate: sDate ? moment(sDate) : null,
        startTime: sTime,
        endDate: eDate ? moment(eDate) : null,
        endTime: eTime,
        startDateString: sDate,
        endDateString: eDate,
        endTimeString: eTime,
        selectedSpanType: sDate && eDate ? 'calendar' : 'advanced',
        startTimeError: null,
        endTimeError: null,
        startDateError: false,
        endDateError: null,
        error: undefined,
        loading: false
    })

    const applyChanges = (definedSpan: string): void => {
        setState({ loading: true })
        validatePipeExpression(definedSpan)
            .then(() => {
                headerContext.setTimespan(definedSpan, undefined, true)
                headerContext.toggleOpen()
                updateIdentityLookup(headerContext, [])
                changeSpanPickerConfig({
                    ...operationsPickerConfigs,
                    advancedMode: true,
                })
                let details = {
                    selectedItem: {
                        label: definedSpan
                    },
                    selectedTimespan: definedSpan,
                    checkedItemsLabels: []
                }
                dispatchRefreshEvent(details)
            })
            .catch(res => {
                setState({ error: res.data, loading: false })
            })
    }

    const setDateFromSpan = () => {
        const selectedSpanType = state.selectedSpanType
        const span = definedSpan

        if (
            selectedSpanType === 'defaultOption' ||
            (selectedSpanType !== 'advanced' && selectedSpanType !== 'calendar')
        ) {
            validatePipeExpression(span).then(res => {
                const startDate = moment(res.data.start)
                const endDate = moment(res.data.end)
                setState(state => ({
                    ...state,
                    selectedSpanType: 'calendar',
                    startDate: startDate,
                    endDate: endDate,
                    startDateString: startDate.format('YYYY-MM-DD'),
                    endDateString: endDate.format('YYYY-MM-DD'),
                    startTime: startDate.format('HH:mm'),
                    endTime: endDate.format('HH:mm')
                }))
            })
        }
    }

    const clearCalendar = (selected: string) => {
        setState({
            startDate: null,
            startTime: null,
            endDate: null,
            endTime: null,
            startDateString: null,
            endDateString: null,
            selectedSpanType: selected,
            startTimeError: null,
            endTimeError: null,
            startDateError: null,
            endDateError: null
        })
    }

    const onChangeEndTime = e => {
        const value = e.target.value
        const Time = moment(value, 'HH:mm')
        const validTimeString = value.replace('_', '').length === 5
        setState(state => ({
            ...state,
            selectedSpanType: 'calendar',
            endTime: value,
            endTimeError: (!Time.isValid() || !validTimeString) && value.length
        }))
    }

    const onChangeEndDate = e => {
        const value = e.target.value
        const date = moment(value)
        setState(state => ({
            ...state,
            selectedSpanType: 'calendar',
            endDateString: value,
            endDate: date.isValid() && date > state.startDate ? date : state.endDate,
            endDateError: !date.isValid()
        }))
    }

    const onChangeStartDate = e => {
        const value = e.target.value
        const date = moment(value)
        setState(state => ({
            ...state,
            selectedSpanType: 'calendar',
            startDateString: value,
            startDate: date.isValid() && (date < state.endDate || !state.endDate) ? date : state.startDate,
            startDateError: !date.isValid()
        }))
        applySpanChange()
    }

    const onChangeStartTime = e => {
        const value = e.target.value
        const Time = moment(value, 'HH:mm')
        const validTimeString = value.replace('_', '').length === 5
        setState(state => ({
            ...state,
            selectedSpanType: 'calendar',
            startTime: value,
            startTimeError: (!Time.isValid() || !validTimeString) && value.length
        }))
    }

    const onChangeAdvanced = () => {
        const startDate = state.startDate || ''
        const endDate = state.endDate || startDate
        const startTime = state.startTime || ''
        const endTime = state.endTime || ''
        const span =
            state.selectedSpanType === 'calendar' && startDate
                ? customDatesExpression(startDate, endDate, startTime, endTime)
                : null

        setDefinedSpan(span || definedSpan)

        // clearCalendar('advanced')
        setTimeout(() => {
            document.querySelector('input[name="timespan-expression"]').focus()
        }, 250)
    }

    const selectDate = e => {
        const tempo = moment(e)
        setState(state => {
            const resetTimeConditional =
                (state.startDate && state.startDate._i === tempo._i) ||
                (state.startDate && !state.endDate && tempo._i >= state.startDate._i)
            const resetStartDateConditional = state.startDate && !state.endDate && tempo._i >= state.startDate._i
            const resetEndDateConditional =
                state.startDate &&
                ((!state.endDate && tempo._i > state.startDate._i) || tempo._i === state.startDate._i)

            return {
                ...state,
                selectedSpanType: 'calendar',
                startDate: resetStartDateConditional ? state.startDate : tempo,
                startDateError: resetStartDateConditional
                    ? !tempo.isValid()
                    : state.startDate && !state.startDate.isValid(),
                startDateString: resetStartDateConditional ? state.startDateString : tempo.format('YYYY-MM-DD'),
                endDate: resetEndDateConditional ? tempo : null,
                endDateError: resetEndDateConditional ? !tempo.isValid() : state.endDate && !state.endDate.isValid(),
                endDateString: tempo.format('YYYY-MM-DD'),
                startTime: resetTimeConditional ? '00:00' : state.startTime,
                startTimeError: resetTimeConditional ? false : state.startTimeError,
                endTime: resetTimeConditional ? '23:59' : state.endTime,
                endTimeError: resetTimeConditional ? false : state.endTimeError
            }
        })
    }

    const _onFail = res => {
        setState({
            error: res.data,
            loading: false
        })
    }

    const _onSuccess = (res, span, selectedSpanType) => {
        const self = this
        // if (self.props.open) self.props.toggleDropdown()
        if (!span) span = res.data.explained
        // clearCalendar(selectedSpanType)
        // onChangeSpan(span, selectedSpanType)
        setDefinedSpan(span)
    }

    const applySpanChange = () => {
        const startDate = state.startDate || ''
        const endDate = state.endDate || startDate
        const startTime = state.startTime || ''
        const endTime = state.endTime || ''
        const selectedSpanType = state.selectedSpanType
        const span =
            selectedSpanType === 'advanced'
                ? definedSpan
                : customDatesExpression(startDate, endDate, startTime, endTime)
        validatePipeExpression(span)
            .then(e => {
                _onSuccess(e, span, selectedSpanType)
            })
            .catch(e => {
                _onFail(e)
            })
    }

    return (
        <>
            <LiveModal.Header>
                <LiveModal.Title>{i18n('Define a timespan')}</LiveModal.Title>
            </LiveModal.Header>
            <LiveModal.Body>
                <ModalBody>
                    <Wrapper
                        theme={'light'} // the modal does not cover the dark theme yet
                    >
                        <div className="calendar-content">
                            <CustomCalendar
                                startDate={state.startDate}
                                endDate={state.endDate}
                                startTime={state.startTime}
                                endTime={state.endTime}
                                selectedSpanType={state.selectedSpanType}
                                selectDate={selectDate}
                                onChangeStartDate={onChangeStartDate}
                                onChangeStartTime={onChangeStartTime}
                                onChangeEndDate={onChangeEndDate}
                                onChangeEndTime={onChangeEndTime}
                                startDateString={state.startDateString}
                                endDateString={state.endDateString}
                                startDateError={state.startDateError}
                                endDateError={state.endDateError}
                                startTimeError={state.startTimeError}
                                endTimeError={state.endTimeError}
                                setDateFromSpan={setDateFromSpan}
                            ></CustomCalendar>
                        </div>

                        <button
                            style={{ margin: '10px' }}
                            className="btn btn-primary btn-bold"
                            onClick={applySpanChange}
                        >
                            apply
                        </button>
                        <br />

                        <SpanInputStyled>
                            <SpanInput
                                type="text"
                                onSelect={onChangeAdvanced}
                                onChange={e => {
                                    setDefinedSpan(e.target?.value)
                                    setState(state => ({
                                        ...state,
                                        selectedSpanType: 'advanced',
                                        error: null
                                    }))
                                }}
                                value={definedSpan}
                                hasError={typeof state.error?.message != 'undefined'}
                                autoFocus
                                disabled={false}
                                isBelow
                            />
                        </SpanInputStyled>
                    </Wrapper>
                </ModalBody>
            </LiveModal.Body>
            <LiveModal.Footer>
                <FooterActions>
                    <button className="btn btn-primary btn-bold" onClick={() => applyChanges(definedSpan)}>
                        Refresh
                    </button>
                    <span>{i18n('or')}</span>
                    <a onClick={() => headerContext.toggleOpen()}>{i18n('Cancel')}</a>
                    <a
                        className="mode-selector"
                        onClick={() => headerContext.setAdvancedMode(!headerContext.advancedMode)}
                    >
                        {i18n('Show Drilling Operations')}
                    </a>
                </FooterActions>
            </LiveModal.Footer>
        </>
    )
}

export default TimespanSelect

const SpanInputStyled = styled.div`
    padding: 0px 10px;   
`